import { useLocation, useNavigate } from "react-router-dom";
import useAuthentication from "../../../hooks/useAuthentication";
import { useForm } from "react-hook-form";
import {
  initialFormData,
  MasterFormDataType,
  AccountcategoryTableDataType,
} from "./types";
import usePopup from "../../../hooks/usePopup";
import { useMutation } from "react-query";
import { saveMasterData } from "./services";
import { useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { CustomFormField } from "../../../utilis/component/CustomFormElement";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const pageTitle = "Account Category";
function AddOrEditAccountCategory() {
  const { user } = useAuthentication();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm<MasterFormDataType>({
    defaultValues: initialFormData,
  });
  const {
    state: { data = null },
  } = useLocation() as { state: { data: AccountcategoryTableDataType } };
  const { showSnackbar } = usePopup();
  const handleGoBack = () => {
    navigate(-1);
  };


  const { mutate: save, isLoading } = useMutation(saveMasterData, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          duration: 3000,
        });
        handleGoBack();
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message:
          e?.response?.data?.responseMessage ||
          e?.response?.data?.errorMessage ||
          "Something went wrong !",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });
  const onSubmit = (value: MasterFormDataType) => {
    save([
      {
        ...value,
        accountType: Number(value?.accountType?.accountType || 0),
        clinicId: Number(user?.clinicId || 0),
        createdByUserId: Number(user?.groupId || 0),
        categoryId: data?.categoryId || null,
      },
    ]);
  };

  const handleReset = () => {
    reset(initialFormData);
  };

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        accountType:accountType[data?.accountType as keyof typeof accountType]
      });
    }
  }, [data]);

  return (
    <div className="p-3">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper>
          <div className="flex justify-between items-center p-2">
            <div>
              <Typography variant="h6" className="p-3">
                {data ? "Update" : "Add"} {pageTitle}
              </Typography>
            </div>
            <div className="flex gap-2">
              <Button
                disabled={isLoading}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
              >
                back
              </Button>
              {!Boolean(data) && (
                <Button
                  disabled={isLoading}
                  color="error"
                  type="reset"
                  onClick={handleReset}
                  startIcon={<CloseIcon />}
                >
                  reset
                </Button>
              )}
              <Button
                disabled={isLoading}
                type="submit"
                color="success"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="success" />
                  ) : (
                    <SaveIcon />
                  )
                }
              >
                {data ? "update" : "save"}
              </Button>
            </div>
          </div>
          <Divider />
          <div className="p-3 grid grid-cols-4 gap-3">
            <CustomFormField
              control={control}
              name="categoryCode"
              element="input"
              fieldProps={{ label: "Enter category code " }}
              rules={{ required: "Code is required" }}
            />
       
            <CustomFormField
              control={control}
              name="categoryName"
              element="input"
              fieldProps={{ label: "Enter category name " }}
              rules={{ required: "Name is required" }}
            />

                 <CustomFormField
              control={control}
              name="accountType"
              element="autocomplete"
              options={Object.entries(accountType).map(item=>item[1]) || []}
              autocompleteProps={{
                getOptionLabel: (option) => option.accountName,
                isOptionEqualToValue: (option, value) => option.accountType == value.accountType,
              }}
              fieldProps={{ label: "Enter account type" }}
              rules={{ required: "Account type is required" }}
            />

            <CustomFormField
              control={control}
              name="categoryStatus"
              element="radio"
              radioProps={{ row: true }}
              radios={[
                { label: "Active", value: 1 },
                { label: "Inactive", value: 0 },
              ]}
              fieldProps={{ label: "Status " }}
              rules={{ required: "Status is required" }}
            />
          </div>
        </Paper>
      </form>
    </div>
  );
}

export default AddOrEditAccountCategory;

export const accountType:{
  [key: number]: { accountType: number; accountName: string };
} = {
  1: { accountType: 1, accountName: "Expense" },
  2: { accountType: 2, accountName: "Income" },
  3: { accountType: 3, accountName: "Current Assets" },
  4: { accountType: 4, accountName: "Fixed Assets" },
  5: { accountType: 5, accountName: "Tangible Assets" },
  6: { accountType: 6, accountName: "Intangible Assets" },
  7: { accountType: 7, accountName: "Operating Assets" },
  8: { accountType: 8, accountName: "Non-Operating Assets" },
  9: { accountType: 9, accountName: "Loans" },
  10: { accountType: 10, accountName: "Accounts Payable" },
  11: { accountType: 11, accountName: "Mortgages" },
  12: { accountType: 12, accountName: "Deferred Revenue" },
  13: { accountType: 13, accountName: "Bonds" },
  14: { accountType: 14, accountName: "Warranties" },
  15: { accountType: 15, accountName: "Accrued Expenses" },
  16: { accountType: 16, accountName: "Common Stock" },
  17: { accountType: 17, accountName: "Preferred Stock" },
  18: { accountType: 18, accountName: "Additional Paid In Capital" },
  19: { accountType: 19, accountName: "Treasury Stock" },
  20: { accountType: 20, accountName: "Retained Earnings" },
  21: { accountType: 21, accountName: "Employee Salary & Benefits" },
  22: { accountType: 22, accountName: "Advertising" },
  23: { accountType: 23, accountName: "Rent" },
  24: { accountType: 24, accountName: "Utilities" },
  25: { accountType: 25, accountName: "Inventory & Equipment Purchase" },
  26: { accountType: 26, accountName: "Travel Reimbursement" },
  27: { accountType: 27, accountName: "Others" },
  28: { accountType: 28, accountName: "Professional Income" },
  29: { accountType: 29, accountName: "Sales Account" },
  30: { accountType: 30, accountName: "Interest" },
  31: { accountType: 31, accountName: "Dividend" },
};

